import * as React from 'react';
import { connect } from 'react-redux';
import intersection from 'lodash/intersection';

import PageNotFound from 'components/page/page-not-found/PageNotFound';
import TemporarySessionProvider from 'components/temporary-session/TemporarySessionProvider';

import { terminateSession } from 'modules/access/reducers/session';

import { getBrandIdsWithPermissionToAddBrandsToCrawler } from 'utils/access';
import { isAdmin } from 'utils/account';
import {
  ORGANIZATION_TYPE_CCM_ONLY,
  isBrandsFocusedOrganization,
  isCcmMixedFocusedOrganization,
  isCcmOnlyFocusedOrganization,
  isConnectedToBrandAgencyOrganization,
  matchType,
  organizationHasApprovedProfile,
  organizationHasNotPendingProfile
} from 'utils/organization';
import { ACCESS_DENIED_STATUS_CODE, NOT_FOUND_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from 'utils/status';

import type { Account, AuthenticatedProtection, Organization, Role } from 'types';

type OwnProps = {};

type StateProps = {
  status: number,
  currentOrganization: Organization,
  currentAccount: Account,
  hasAccessToMiscellaneous: boolean,
  roles: Array<Role>
};

type DispatchProps = {
  dispatchTerminateSession: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

export function authenticated(
  Component: React.ComponentType<any>,
  protection?: AuthenticatedProtection
) {
  class AuthenticatedComponent extends React.PureComponent<Props> {
    componentDidMount() {
      this.checkStatus(this.props.status);
    }

    componentDidUpdate(prevProps: Props) {
      const { status: prevStatus } = prevProps;
      const { status: nextStatus } = this.props;

      if (!nextStatus || prevStatus === nextStatus) {
        return;
      }

      this.checkStatus(nextStatus);
    }

    isAllowedUser(): boolean {
      const { currentOrganization, currentAccount, hasAccessToMiscellaneous, roles } = this.props;
      const {
        adminOnly,
        allowOnly,
        allowRoleOnly,
        allowOnlyForApprovedProfile,
        allowForFreemiumBrandProfile,
        allowForCcmOnlyBrandProfile,
        protectFrom,
        protectFromPendingProfiles,
        protectFromAgencyChild,
        protectFromNonCcmBrands,
        withAccessToMiscellaneous,
        allowForRoles
      } = protection || {};

      const isCcmOnlyOrganization = isCcmOnlyFocusedOrganization(currentOrganization);
      const isCcmMixedOrganization = isCcmMixedFocusedOrganization(currentOrganization);
      const isInfluencer = !isBrandsFocusedOrganization(currentOrganization);
      const hasAdminFriendlyRole = intersection(roles, allowForRoles ?? []).length > 0;
      const protectedFromCcmOnly = (protectFrom || []).includes(ORGANIZATION_TYPE_CCM_ONLY);

      if (isCcmOnlyOrganization && protectedFromCcmOnly && !isAdmin(currentAccount, roles)) {
        return false;
      }

      if (protectFromNonCcmBrands && !isCcmOnlyOrganization && !isCcmMixedOrganization) {
        return false;
      }

      if (hasAdminFriendlyRole) {
        return true;
      }

      // role only. no admins.
      if (!hasAdminFriendlyRole && allowRoleOnly) {
        return false;
      }

      if (isAdmin(currentAccount, roles) && !isInfluencer) {
        return true;
      }

      if (allowOnly && !matchType(currentOrganization, allowOnly)) {
        return false;
      }

      if (adminOnly && !isAdmin(currentAccount, roles)) {
        return false;
      }

      if (!allowForFreemiumBrandProfile && currentOrganization?.isFreemium) {
        return false;
      }

      if (protectFromAgencyChild && currentOrganization && isConnectedToBrandAgencyOrganization(currentOrganization)) {
        return false;
      }

      if (protectFrom && matchType(currentOrganization, protectFrom)) {
        return false;
      }

      // COLLAB-12525
      if (!currentOrganization?.isFreemium && !allowForCcmOnlyBrandProfile && allowOnlyForApprovedProfile && !organizationHasApprovedProfile(currentOrganization)) {
        return false;
      }

      // COLLAB-14898
      if (protectFromPendingProfiles && !organizationHasNotPendingProfile(currentOrganization)) {
        return false;
      }

      if (withAccessToMiscellaneous && !hasAccessToMiscellaneous) {
        return false;
      }

      return true;
    }

    checkStatus(status: number): void {
      const { dispatchTerminateSession } = this.props;

      if (status === UNAUTHORIZED_STATUS_CODE) {
        dispatchTerminateSession();
      }
    }

    render(): React.ReactNode {
      const { currentAccount, status } = this.props;

      // @todo: deprecate this condition?
      if (status === NOT_FOUND_STATUS_CODE || status === ACCESS_DENIED_STATUS_CODE) {
        return (
          <PageNotFound />
        );
      }

      if (!this.isAllowedUser()) {
        return (
          <PageNotFound />
        );
      }

      const hasTemporarySession = currentAccount?.hasTemporarySession;

      return (
        <React.Fragment>
          {
            hasTemporarySession && (
              <TemporarySessionProvider />
            )
          }
          <Component {...this.props} />
        </React.Fragment>
      );
    }
  }

  const mapStateToProps = ({ common, account, organizations }): StateProps => {
    const currentOrganization = organizations.currentOrganization;
    let brandProfileId: number | null = null;

    if (isBrandsFocusedOrganization(currentOrganization) && currentOrganization.brandProfiles) {
      brandProfileId = currentOrganization.brandProfiles.length
        ? currentOrganization.brandProfiles[0].id
        : null;
    }

    const brandsWithAccessToMiscellaneous = getBrandIdsWithPermissionToAddBrandsToCrawler();
    const hasAccessToMiscellaneous = brandProfileId !== null
      && brandsWithAccessToMiscellaneous.includes(brandProfileId);

    return {
      currentOrganization,
      hasAccessToMiscellaneous,
      status: common.notification.status,
      currentAccount: account.currentAccount,
      roles: account.roles
    };
  };

  const mapDispatchToProps = (dispatch): DispatchProps => ({
    dispatchTerminateSession: () => dispatch(terminateSession())
  });

  return connect(mapStateToProps, mapDispatchToProps)(AuthenticatedComponent);
}
